// Accounting › Money owed (Blueprint's), its own module out of Books.tsx, on ui-web's DashboardPage
// (patterns/README.md, BL-189), one column. The title block's figures are how many invoices are open, the
// unapplied credit and the deposits on sold estimates, each only when there is one. Then the aging (the
// catalogued Aging, without its late invoices: the list under it has them all), saying at its end whether
// it ties to AR, a difference a red row at its top. Then today's reminders, then the open invoices (the
// overdue first, or all), each a RowList row whose Record payment opens #420's RecordPayment under it and
// whose More has its statement and the collections hold (a reason, inline). Then the financed sales the
// lender hasn't paid out (BL-005), each with its payout form. The reads and the actions are Receivables.tsx's;
// Riverstone keeps it. receivables-page.css lays out the forms, tokens only.
import { Aging, Button, Figures, Link, RowList, Segmented, TextField, type Row } from '@flowology/ui-web/fl';
import { DashboardPage, type DashboardSection } from '@flowology/ui-web/patterns';
import { useEffect, useRef, useState } from 'react';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { DocLink } from '../shared/DocLink.tsx';
import { useMirror } from '../shared/mirror.ts';
import type { FinancingWaiting } from './financing-model.ts';
import { PayoutForm } from './FinancingWaiting.tsx';
import { arTie, dayText, money, todayIn } from './model.ts';
import { officeHost } from './office-host.ts';
import { BOOKS_PARTS } from './parts.ts';
import type { BooksPart } from './props.ts';
import { RecordPayment } from './RecordPayment.tsx';
import { downloadStatement } from './statement-pdf.ts';
import type { ArAging, BankAccount, DueReminder } from './types.ts';
import { useAct, useQuery, type Act } from './ui.tsx';
import './receivables-page.css';

const PART = BOOKS_PARTS.find((p) => p.part === 'receivables')!;
type Invoice = ArAging['rows'][number];
const days = (n: number) => `${n} day${n === 1 ? '' : 's'}`;
/** "text and email": a reminder's channels, as the office says them. */
const channelWords = (channels: readonly string[]) =>
  channels.map((c) => (c === 'sms' ? 'text' : c)).join(' and ');

/** Putting a customer on the collections hold, or releasing it, under the invoice's row: a reason first. */
function HoldForm({ row, act, onDone }: { row: Invoice; act: Act; onDone: () => void }) {
  const [reason, setReason] = useState('');
  return (
    <div className="bp-ar-form">
      <div className="bp-ar-full">
        <TextField
          label={row.onHold ? 'Why release the hold?' : 'Why put them on hold?'}
          value={reason}
          onChange={setReason}
          placeholder="A reason is required"
        />
      </div>
      <div className="bp-ar-acts">
        <Button
          variant="primary"
          size="touch-on-phone"
          isDisabled={act.busy || !reason.trim()}
          onPress={() =>
            void (async () => {
              const ok = await act.run(
                'customers.setCollectionsHold',
                { customerId: row.customerId, on: !row.onHold, reason: reason.trim() },
                row.onHold ? 'Hold released.' : 'On hold: no reminders, and booking shows a banner.',
              );
              if (ok) onDone();
            })()
          }
        >
          {row.onHold ? 'Release hold' : 'Put on hold'}
        </Button>
        <Link size="touch-on-phone" onPress={onDone}>
          Cancel
        </Link>
      </div>
    </div>
  );
}

/** What doesn't tie, as red rows at the top of the aging: AR itself, then the credits and the deposits (BL-006). */
function tieProblems(a: ArAging, state: ReturnType<typeof arTie>): Row[] {
  if (state.state === 'posting') return [];
  const rows: Row[] = [];
  if (state.state === 'differs')
    rows.push({
      id: 'ar',
      name: 'Accounts receivable',
      word: { text: `Differs from AR by ${money(a.differenceCents)}`, tone: 'bad' },
      ...(state.failed ? { lines: [{ text: 'A posting failed and the team has been told.' }] } : {}),
    });
  if (a.creditsDifferenceCents)
    rows.push({
      id: 'credits',
      name: 'Customer credits',
      word: {
        text: `${money(Math.abs(a.creditsDifferenceCents))} ${a.creditsDifferenceCents > 0 ? 'less' : 'more'} than their account`,
        tone: 'bad',
      },
    });
  if (a.depositsDifferenceCents)
    rows.push({
      id: 'deposits',
      name: 'Customer deposits',
      word: {
        text: `${money(Math.abs(a.depositsDifferenceCents))} ${a.depositsDifferenceCents > 0 ? 'less' : 'more'} than the Customer deposits account`,
        tone: 'bad',
      },
    });
  return rows;
}

function reminderRow(r: DueReminder, act: Act): Row {
  const how = channelWords(r.channels);
  return {
    id: r.invoiceId,
    name: <DocLink doc={{ kind: 'invoice', id: r.invoiceId }}>{r.number}</DocLink>,
    label: `${r.number} · ${r.customerName}`,
    after: { text: ` ${r.customerName}` },
    lines: [
      {
        text: `${money(r.balanceCents)} · ${days(r.daysPastDue)} overdue${how ? ` · ${how}` : ''}${
          r.attachStatement && r.channels.includes('email') ? ' · statement attached' : ''
        }`,
      },
      // why it can't be sent (call instead), where Send now would be
      ...(r.cannotSend ? [{ text: r.cannotSend }] : []),
    ],
    ...(r.cannotSend
      ? {}
      : {
          actions: [
            {
              id: 'send',
              text: 'Send now',
              label: `Send the reminder for ${r.number} now`,
              isDisabled: act.busy,
              onPress: () =>
                void act.run(
                  'receivables.sendReminder',
                  { invoiceId: r.invoiceId, step: r.step },
                  `Reminder sent by ${how}.`,
                ),
            },
          ],
        }),
  };
}

export function ReceivablesPage({
  nav,
  held,
  toast,
  go,
  timeZone,
}: {
  nav: NavProps;
  held: readonly string[];
  toast: (m: string) => void;
  go: (p: BooksPart) => void;
  timeZone?: string;
}) {
  const aging = useQuery<ArAging>('receivables.aging', {});
  const due = useQuery<{ reminders: DueReminder[] }>('receivables.dueReminders', {});
  // a payment recorded here: financing reads again, so a financed one shows at once (BL-005)
  const [paid, setPaid] = useState(0);
  const financing = useQuery<{ payments: FinancingWaiting[] }>('payments.financingWaiting', {}, [paid]);
  const banks = useQuery<{ bankAccounts: BankAccount[] }>('bankAccounts.list', {});
  const act = useAct(toast, async () => {
    await Promise.all([aging.reload(), due.reload()]);
  });
  const [open, setOpen] = useState<{ id: string; form: 'pay' | 'hold' } | null>(null);
  const [payout, setPayout] = useState<string | null>(null);
  const mirroring = useMirror();
  const [filter, setFilter] = useState<'all' | 'overdue'>('overdue');
  const crumb = usePageCrumb(nav);

  // A payment or an invoice just recorded posts to the ledger a moment later (QA-508): while one is on
  // its way, read again every 2 s, up to 30 s, so the tie settles without anyone refreshing.
  const tie = aging.data ? arTie(aging.data) : null;
  const tries = useRef(0);
  useEffect(() => {
    if (tie?.state !== 'posting') {
      tries.current = 0;
      return;
    }
    if (tries.current >= 15) return;
    const t = setTimeout(() => {
      tries.current += 1;
      void aging.reload();
    }, 2000);
    return () => clearTimeout(t);
  }, [aging.data]);

  const a = aging.data;
  const figures = a
    ? [
        ...(a.rows.length ? [{ label: 'Open invoices', value: String(a.rows.length) }] : []),
        ...(a.unappliedCreditsCents
          ? [{ label: 'Unapplied credit', value: money(a.unappliedCreditsCents) }]
          : []),
        ...(a.depositsCents ? [{ label: 'Deposits on sold estimates', value: money(a.depositsCents) }] : []),
      ]
    : [];
  const unread = (what: string): Pick<DashboardSection, 'status' | 'failedText' | 'onRetry' | 'what'> =>
    aging.error
      ? { status: 'failed', failedText: aging.error, onRetry: () => void aging.reload() }
      : { status: 'loading', what };

  const agingSection: DashboardSection =
    a && tie
      ? {
          title: 'Aging',
          state:
            tie.state === 'ties'
              ? { mark: 'done', word: 'Ties to AR' }
              : tie.state === 'posting'
                ? { mark: 'waiting', word: 'Posting what was just recorded…' }
                : { mark: 'problem', word: `Differs from AR by ${money(a.differenceCents)}` },
          children: (
            <>
              <RowList label="What doesn’t tie" rows={tieProblems(a, tie)} />
              <Aging data={a} host={officeHost(go)} late={false} />
            </>
          ),
        }
      : { title: 'Aging', ...unread('who owes you') };

  const reminders: DashboardSection = due.data
    ? {
        title: 'Reminders due today',
        count: due.data.reminders.length,
        children: (
          <RowList
            label="Reminders due today"
            rows={due.data.reminders.map((r) => reminderRow(r, act))}
            empty="No reminder is due today."
          />
        ),
      }
    : due.error
      ? {
          title: 'Reminders due today',
          status: 'failed',
          failedText: due.error,
          onRetry: () => void due.reload(),
        }
      : { title: 'Reminders due today', status: 'loading', what: 'today’s reminders' };

  const invoiceRow = (r: Invoice): Row => ({
    id: r.invoiceId,
    name: <DocLink doc={{ kind: 'invoice', id: r.invoiceId }}>{r.number}</DocLink>,
    label: `${r.number} · ${r.customerName}`,
    after: { text: ` ${r.customerName}` },
    lines: [
      { text: `Due ${dayText(r.dueOn)}` },
      // its own line, red, as the due line's end was: beside the number it squeezed the number at 390
      ...(r.daysPastDue > 0 ? [{ word: `${days(r.daysPastDue)} overdue`, tone: 'bad' as const }] : []),
      ...(r.onHold ? [{ word: 'On hold', tone: 'wait' as const }] : []),
      ...(open?.id === r.invoiceId && open.form === 'pay'
        ? [
            {
              part: (
                <RecordPayment
                  customerId={r.customerId}
                  customerName={r.customerName}
                  firstInvoiceId={r.invoiceId}
                  {...(timeZone ? { timeZone } : {})}
                  dates={held.includes('invoices.edit')}
                  invoices={a!.rows.filter((x) => x.customerId === r.customerId)}
                  toast={toast}
                  onDone={(recorded) => {
                    setOpen(null);
                    if (recorded) {
                      void Promise.all([aging.reload(), due.reload()]);
                      setPaid((n) => n + 1);
                    }
                  }}
                />
              ),
            },
          ]
        : []),
      ...(open?.id === r.invoiceId && open.form === 'hold'
        ? [{ part: <HoldForm row={r} act={act} onDone={() => setOpen(null)} /> }]
        : []),
    ],
    end: { figure: money(r.balanceCents), ...(r.daysPastDue > 30 ? { tone: 'bad' as const } : {}) },
    actions: [
      // the source system takes the payments while the company mirrors it (BL-088)
      ...(mirroring
        ? []
        : [{ id: 'pay', text: 'Record payment', onPress: () => setOpen({ id: r.invoiceId, form: 'pay' }) }]),
      {
        id: 'statement',
        text: 'Statement',
        onPress: () => void downloadStatement(r.customerId, r.customerName, toast),
      },
      {
        id: 'hold',
        text: r.onHold ? 'Release hold' : 'Hold',
        onPress: () => setOpen({ id: r.invoiceId, form: 'hold' }),
      },
    ],
  });

  const shown = a ? (filter === 'overdue' ? a.rows.filter((r) => r.daysPastDue > 0) : a.rows) : [];
  const notDue = a ? a.rows.length - shown.length : 0;
  const invoices: DashboardSection = a
    ? {
        title: 'Open invoices',
        action: (
          <Segmented
            label="Show"
            items={[
              { id: 'overdue', label: 'Overdue' },
              { id: 'all', label: 'All' },
            ]}
            selectedKey={filter}
            onSelectionChange={(k) => setFilter(k as 'all' | 'overdue')}
          />
        ),
        children: (
          <>
            {/* Overdue first, but never hiding that more are open: a just-sent invoice isn't due yet */}
            {filter === 'overdue' && notDue > 0 && (
              <p className="bp-ar-say bp-ar-more">
                {notDue === 1
                  ? '1 more open invoice isn’t due yet.'
                  : `${notDue} more open invoices aren’t due yet.`}{' '}
                <Link onPress={() => setFilter('all')}>Show all</Link>
              </p>
            )}
            <RowList
              label="Open invoices"
              rows={shown.map(invoiceRow)}
              empty={filter === 'overdue' ? 'Nothing is overdue.' : 'No open invoices.'}
            />
          </>
        ),
      }
    : { title: 'Open invoices', ...unread('the open invoices') };

  // BL-005: financed sales the lender hasn't fully paid out; nothing shows when nothing waits
  const waiting = financing.data?.payments ?? [];
  const funding: DashboardSection[] = waiting.length
    ? [
        {
          title: 'Financing waiting to fund',
          children: (
            <RowList
              label="Financing waiting to fund"
              rows={waiting.map((r): Row => ({
                id: r.paymentId,
                name: r.customerName,
                lines: [
                  {
                    text: `loan ${r.reference} · signed ${dayText(r.receivedOn)} · sale ${money(r.amountCents)}${
                      r.fundedCents > 0 ? ` · paid out ${money(r.fundedCents)}` : ''
                    }`,
                  },
                  ...(payout === r.paymentId
                    ? [
                        {
                          part: (
                            <PayoutForm
                              row={r}
                              banks={banks.data?.bankAccounts ?? []}
                              today={todayIn(timeZone)}
                              toast={toast}
                              onDone={(recorded) => {
                                setPayout(null);
                                if (recorded) void financing.reload();
                              }}
                            />
                          ),
                        },
                      ]
                    : []),
                ],
                end: { figure: money(r.owedCents), sub: 'owed' },
                actions: [
                  {
                    id: 'payout',
                    text: 'Record payout',
                    label: `Record a payout for ${r.customerName}`,
                    onPress: () => setPayout(payout === r.paymentId ? null : r.paymentId),
                  },
                ],
              }))}
            />
          ),
        },
      ]
    : [];

  return (
    <div className="app">
      <main>
        <PageBanners nav={nav} />
        <DashboardPage
          head={{
            ...(crumb ? { crumb } : {}),
            title: PART.bp ?? PART.rail,
            lede: PART.lede,
            ...(figures.length ? { figures: <Figures items={figures} /> } : {}),
          }}
          main={[agingSection, reminders, invoices, ...funding]}
        />
      </main>
    </div>
  );
}
